import React, { Fragment, ReactEventHandler, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import * as uuid from 'uuid'

import Modal from '../components/basics/Modal/Modal'

function createDeferred<T>() {
  let resolve = (_v: T) => {}
  let reject = (_reason?: any) => {}
  const p = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })
  return [p, resolve, reject] as const
}

interface DialogInternal<RESULT = string> {
  hookInstanceId: symbol
  dialogKey: string
  element: ReactNode
  p: Promise<RESULT>
  resolve: (r: RESULT) => void
  reject: (r?: any) => void
  onClose: (e?: Parameters<ReactEventHandler<HTMLDialogElement>>[0]) => void
}

type DialogElementInit<T> = ReactNode | ((close: (result: T) => void) => ReactNode)
type DialogOnCloseHandler<T> = (e?: Parameters<ReactEventHandler<HTMLDialogElement>>[0]) => T

interface DialogContext {
  showDialog<T = string>(key: symbol, e: DialogElementInit<T>, handleClose?: DialogOnCloseHandler<T>): DialogInternal<T>
  cancelDialogs(key: symbol): void
}

const dialogContext = React.createContext<DialogContext>({
  showDialog: () => {
    throw new Error('Missing DialogContext.Provider')
  },
  cancelDialogs: () => {
    throw new Error('Missing DialogContext.Provider')
  },
})

export const DialogProvider = React.memo(function DialogProvider({ children }: React.PropsWithChildren) {
  const [dialogs, setDialogs] = useState([] as DialogInternal[])

  // Show a dialog
  const showDialog = useCallback(
    function <T>(hookInstanceId: symbol, elementInit: DialogElementInit<T>, handleClose?: DialogOnCloseHandler<T>) {
      const [p, resolve, reject] = createDeferred<T>()

      const close = (result: T) => {
        setDialogs((prev) => prev.filter(({ dialogKey }) => dialogKey !== d.dialogKey))
        resolve(result)
      }

      const element = typeof elementInit === 'function' ? elementInit(close) : elementInit

      const d: DialogInternal<T> = {
        hookInstanceId,
        dialogKey: uuid.v4(),
        element,
        p,
        resolve,
        reject,
        onClose: (e?) => {
          setDialogs((prev) => prev.filter(({ dialogKey }) => dialogKey !== d.dialogKey))
          if (handleClose) {
            return resolve(handleClose(e))
          }
          reject(e?.currentTarget.returnValue || null)
        },
      }

      setDialogs((prev) => [...prev, d as any])

      return d
    },
    [setDialogs]
  )

  // Cancel all dialogs associated with a given hook instance.
  const cancelDialogs = useCallback(
    (hookInstanceId: symbol) =>
      setDialogs((prev) => {
        for (const d of prev) {
          if (d.hookInstanceId === hookInstanceId) {
            d.onClose()
          }
        }
        return prev.filter((d) => d.hookInstanceId !== hookInstanceId)
      }),
    [setDialogs]
  )

  const context = useMemo(() => ({ showDialog, cancelDialogs }), [showDialog, cancelDialogs])

  return (
    <dialogContext.Provider value={context}>
      {children}
      <Outlet />
      {dialogs.map((d) => (
        <Fragment key={d.dialogKey}>
          <Modal onClose={d.onClose}>{d.element}</Modal>
        </Fragment>
      ))}
    </dialogContext.Provider>
  )
})

/**
 * Show dialogs and wait for the result asynchronously.
 *
 * There are two options for the first argument:
 *
 * Either pass in a ReactNode (i.e. `<div/>`). You then have to close the dialog
 * via the DOM APIs, e.g. with a `<form method="dialog">`. You also have to pass
 * in the second argument, or `showForm` will always reject.
 *
 * Or you pass in a function that returns a ReactNode. The function receives a
 * function as its only argument which can be used to close the dialog with a
 * specific result.
 *
 * The optional second argument is a function that will be called when the
 * dialog is closed *except* if the callback from the first argument is used to
 * close the dialog.
 *
 * This happens when
 *   * the dialog is closed via the DOM APIs,
 *   * the user presses escape, or
 *   * the component calling the hook is unmounted.
 *
 * Its return value will be the dialog's result.
 *
 * When the argument is omitted and the dialog is closed, the promise returned
 * by `showForm` will be rejected.
 *
 * @example
 * ```ts
 * const showDialog = useDialog()
 *
 * const handleSubmit = () => {
 *   const result = await showDialog(
 *     (close) => (
 *       <div>
 *         <button onClick={() => close(true)}>Yes!</button>
 *         <button onClick={() => close(false)}>No!</button>
 *       </div>
 *     ),
 *     () => false
 *   )
 *
 *   if (result) {
 *     doIt()
 *   }
 * }
 *
 * ```
 */
export default function useDialog() {
  const ctx = React.useContext(dialogContext)

  const [hookInstanceId] = useState(Symbol())

  useEffect(() => {
    return () => ctx.cancelDialogs(hookInstanceId)
  }, [ctx.cancelDialogs, hookInstanceId])

  return useCallback(
    function <T>(elementInit: DialogElementInit<T>, handleClose?: DialogOnCloseHandler<T>) {
      const d = ctx.showDialog<T>(hookInstanceId, elementInit, handleClose)
      return d.p
    },
    [ctx.showDialog]
  )
}
